import { ReactNode, useCallback } from 'react';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Modal } from '@shared/components/Modal';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { i18n } from '@shared/util/i18n';
import styles from './UpgradeModal.module.css';

export interface UpgradeModalProps {
  title: string;
  description: string;
  isOpen: boolean;
  header?: ReactNode;
  onOpenChange(isOpen: boolean): void;
  onUpgrade(): void;
  onLearnMore?(): void;
}

export function UpgradeModal({
  title,
  description,
  isOpen,
  header,
  onOpenChange,
  onUpgrade,
  onLearnMore,
}: UpgradeModalProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleUpgrade = useCallback(() => {
    close();
    onUpgrade();
  }, [onUpgrade, close]);
  const handleLearnMore = useCallback(() => {
    close();
    onLearnMore?.();
  }, [onLearnMore, close]);

  return (
    <Modal
      mode="modal"
      animate
      isOpen={isOpen}
      lightDismiss
      onOpenChange={onOpenChange}
      testID="upgrade-modal"
      className={styles.upgradeModal}
    >
      <Modal.TitleBar startContent={<Modal.CloseButton onClick={close} />} showBorder={false} />
      <UpgradeContent
        title={title}
        description={description}
        header={header}
        onUpgrade={handleUpgrade}
        onLearnMore={onLearnMore ? handleLearnMore : undefined}
        onDismiss={close}
      />
    </Modal>
  );
}

export interface UpgradeContentProps {
  title: string;
  description: string;
  header?: ReactNode;
  onUpgrade?: () => void;
  onLearnMore?: () => void;
  onDismiss?: () => void;
}

export function UpgradeContent({
  title,
  description,
  header,
  onUpgrade,
  onLearnMore,
  onDismiss,
}: UpgradeContentProps) {
  return (
    <View center gap={3} paddingHorizontal={4} paddingTop={0} paddingBottom={3} marginTop={-2}>
      <View center gap={2}>
        {header ?? <Icon name="lock" className={styles.lockIcon} />}
        <View gap={1} className={styles.textContent}>
          <Text size="body1" weight="semibold" balance>
            {title}
          </Text>
          <Text size="body2" balance>
            {description}
          </Text>
        </View>
      </View>
      <View row gap={1}>
        {!!onLearnMore && (
          <Button
            variant="ghost"
            size="large"
            label={i18n('common.learnMore')}
            onClick={onLearnMore}
          />
        )}
        {!onLearnMore && !!onDismiss && (
          <Button
            variant="ghost"
            size="large"
            label={!!onUpgrade ? i18n('common.close') : i18n('common.ok')}
            onClick={onDismiss}
          />
        )}
        {!!onUpgrade && (
          <Button
            variant="primary"
            size="large"
            label={i18n('common.upgrade')}
            onClick={onUpgrade}
          />
        )}
      </View>
    </View>
  );
}
